import { Frame } from '@/marketing/Frame';
import { Reveal } from '@/lib/motion';

// Illustrative audit record — the "receipt" a single daily run leaves behind.
// Discipline rows carry the same per-stage colours as the engine funnel.
const RECORD: Array<{ k: string; v: string; color?: string }> = [
  { k: 'RUN', v: '2026-07-05 · 06:00 ET' },
  { k: 'UNIVERSE', v: 'S&P 500 · 500 names' },
  { k: 'RANKED', v: 'FinIntellect', color: '#38bdf8' },
  { k: 'CONFIRMED', v: 'TechniQ', color: '#3dff85' },
  { k: 'RISK', v: 'RiskGuard · exposure set', color: '#f7941d' },
  { k: 'VETTED', v: 'SignalForge', color: '#ff4d5e' },
  { k: 'BOOK', v: '20–30 holdings' },
  { k: 'STATE', v: 'versioned · hashed' },
  { k: 'SIGN-OFF', v: 'human governance' },
];

export function GlassBox() {
  return (
    <Frame id="trust" label="Glass box">
      <div className="grid gap-14 lg:grid-cols-[1fr_1fr] lg:gap-20">
        <div>
          <Reveal>
            <h2 className="font-display text-[clamp(2rem,4.5vw,3.25rem)] font-bold leading-[1.03] tracking-[-0.02em] text-foreground">
              Every decision, on the record.
            </h2>
          </Reveal>
          <Reveal delay={0.06}>
            <p className="mt-6 text-lg leading-relaxed text-muted-foreground">
              Most funds are a black box: money goes in, a number comes out. Ours
              is a glass box. For any holding on any day, we can trace which model
              scored it, which filters it passed, what the risk overlay decided and
              what the filings showed — versioned, hashed and fully reconstructable.
            </p>
          </Reveal>
          <Reveal delay={0.12}>
            <div className="mt-10 border-t border-white/8 pt-8">
              <p className="font-display text-2xl font-semibold text-foreground">
                Autonomous execution.{' '}
                <span className="text-[color:var(--nq-gold)]">Human governance.</span>
              </p>
              <p className="mt-3 max-w-md text-[15px] leading-relaxed text-muted-foreground">
                The daily loop runs itself at market speed. Humans control the
                system and sign off every model change through an institutional
                promotion gate — no new model ships because one backtest looked good.
              </p>
            </div>
          </Reveal>
        </div>

        <Reveal delay={0.08}>
          <div className="mkt-glass rounded-2xl p-6 sm:p-7">
            <div className="mb-5 flex items-center justify-between border-b border-white/8 pb-4">
              <span className="mkt-mono text-[11px] uppercase tracking-[0.2em] text-[color:var(--nq-up-bright)]">
                Audit record
              </span>
              <span className="mkt-mono text-[10px] uppercase tracking-[0.18em] text-white/30">
                Illustrative
              </span>
            </div>
            <dl className="flex flex-col gap-2.5">
              {RECORD.map(({ k, v, color }) => (
                <div key={k} className="flex items-baseline gap-3">
                  <dt className="mkt-mono w-24 shrink-0 text-[11px] uppercase tracking-[0.14em] text-white/35">
                    {k}
                  </dt>
                  <dd
                    className="mkt-mono min-w-0 flex-1 truncate text-[13px] text-foreground/90"
                    style={color ? { color } : undefined}
                  >
                    {v}
                  </dd>
                </div>
              ))}
            </dl>
            <div className="mt-5 border-t border-white/8 pt-4">
              <span className="mkt-mono block truncate text-[11px] text-[color:var(--nq-blue-bright)]">
                sha256: a1f9c4e7…d20b · signed
              </span>
            </div>
          </div>
        </Reveal>
      </div>
    </Frame>
  );
}
